import axios from 'axios';
import { Book, Section, Content, ContentType, SectionType, SectionTheme, BookFormData, SectionFormData, ContentFormData } from '../types';

// Use environment variable for production, proxy for development
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    throw error;
  }
);

export const adminApi = {
  // Content Types
  async getContentTypes(): Promise<ContentType[]> {
    const response = await api.get('/api/admin/content-types');
    return response.data.success ? response.data.content_types : [];
  },

  // Section Types
  async getSectionTypes(): Promise<SectionType[]> {
    const response = await api.get('/api/admin/section-types');
    return response.data.success ? response.data.section_types : [];
  },

  // Section Themes
  async getSectionThemes(): Promise<SectionTheme[]> {
    const response = await api.get('/api/admin/section-themes');
    return response.data.success ? response.data.section_themes : [];
  },

  // Books
  async getBooks(): Promise<Book[]> {
    const response = await api.get('/api/admin/books');
    return response.data.success ? response.data.books : [];
  },

  async createBook(book: BookFormData): Promise<Book> {
    const response = await api.post('/api/admin/books', book);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create book');
    }
    return response.data.book;
  },

  // Sections
  async createSection(section: SectionFormData): Promise<Section> {
    const response = await api.post('/api/admin/sections', section);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create section');
    }
    return response.data.section;
  },

  // Content
  async getSectionContent(sectionId: string): Promise<Content[]> {
    const response = await api.get(`/api/admin/content/${sectionId}`);
    return response.data.success ? response.data.content : [];
  },

  async createContent(content: ContentFormData): Promise<Content> {
    const response = await api.post('/api/admin/content', content);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create content');
    }
    return response.data.content;
  },

  async updateContent(id: string, updates: Partial<Content>): Promise<Content> {
    const response = await api.put(`/api/admin/content/${id}`, updates);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update content');
    }
    return response.data.content;
  },

  async deleteContent(id: string): Promise<void> {
    const response = await api.delete(`/api/admin/content/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete content');
    }
  },

  async insertContent(sectionId: string, afterContentId: string, content: Omit<ContentFormData, 'section_id'>): Promise<Content> {
    const response = await api.post('/api/admin/content/insert', {
      section_id: sectionId,
      after_content_id: afterContentId,
      content,
    });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to insert content');
    }
    return response.data.content;
  },

  async reorderContent(contentId: string, direction: 'up' | 'down'): Promise<void> {
    const response = await api.post('/api/admin/content/reorder', {
      content_id: contentId,
      direction,
    });
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to reorder content');
    }
  },
};